/**
 * Ручной набор в пульте: ввёл номер — позвонил, без карточки контакта.
 *
 * Нужен для двух вещей, которые иначе делать нечем: проверить, что звук и линия
 * живы, и перезвонить человеку, который набрал сам и в очереди не лежит.
 *
 * Своей панели звонка здесь нет намеренно: состояние вызова показывает та же
 * CallPanel, что и для звонков по карточке. Вторая панель разошлась бы с первой
 * по таймеру и по кнопке отбоя — диспетчер жал бы «Завершить» у неактивной копии.
 *
 * Приведённый номер показывается до нажатия: диспетчер сверяет глазами то, что
 * реально уйдёт в набор, а не то, что он напечатал. Показ — предпросмотр, набирает
 * пульт номер из ответа сервера: нормализация одна на систему (§6.8), и решает её
 * сервер. Здесь та же функция вызывается только чтобы не гонять заведомо пустой
 * запрос и чтобы человек увидел результат до нажатия.
 *
 * С 24.09.2026 («Свой номер») номер не из сегодняшней базы открывается
 * карточкой — с метками и заявкой; таких номеров в день ограниченное число.
 */

import { useState } from 'react';

import { Button } from '../../../shared/ui/button.js';
import { Field } from '../../../shared/ui/field.js';
import { formatPhone } from '../../../shared/lib/format-phone.js';
import { manualDialErrorText, ownNumbersHint, parseManualPhone } from '../manual-dial.js';

export interface ManualDialFormProps {
  /** Линия есть, вкладка лидер, другой звонок не идёт — то же условие, что у карточки */
  canCall: boolean;
  busy: boolean;
  /** Отказ сервера на прошлый набор; undefined — отказа не было */
  error: string | undefined;
  /**
   * Остаток часового лимита после последнего набора; null — ещё не звонили.
   * Показывается, чтобы отказ по лимиту не стал сюрпризом посреди смены.
   */
  remainingThisHour: number | null;
  /** Остаток своих номеров на сегодня после последнего набора карточкой; null — ещё не набирали. */
  ownNumbers: { left: number; perDay: number } | null;
  /** Ввод уходит как есть: к E.164 его приводит сервер и он же возвращает результат */
  onCall: (input: string) => void;
}

export function ManualDialForm({
  canCall,
  busy,
  error,
  remainingThisHour,
  ownNumbers,
  onCall,
}: ManualDialFormProps): React.JSX.Element {
  const [input, setInput] = useState('');
  // Пока по полю не ушли, ошибку не показываем: красное поле на первой же цифре
  // мешает, а не помогает
  const [touched, setTouched] = useState(false);

  const parsed = parseManualPhone(input);
  // Пустое поле после блюра ошибкой не считается: человек мог просто кликнуть мимо
  const showError = touched && input.trim() !== '' && !parsed.ok;

  const submit = (): void => {
    setTouched(true);
    if (!parsed.ok || !canCall || busy) return;
    onCall(input.trim());
  };

  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-medium text-slate-900">Ручной набор</h2>
        {remainingThisHour === null ? null : (
          <span className="text-sm text-slate-600">
            Осталось в этом часе: {remainingThisHour}
          </span>
        )}
      </div>

      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className="min-w-64 flex-1">
          <Field
            label="Номер"
            inputMode="tel"
            autoComplete="off"
            placeholder="8 999 123-45-67"
            value={input}
            onChange={(event) => { setInput(event.target.value); }}
            onBlur={() => { setTouched(true); }}
            {...(showError
              ? { error: manualDialErrorText(parsed.error) }
              : {
                  hint: parsed.ok
                    ? `Набираю ${formatPhone(parsed.phone)}`
                    : 'Можно 8, +7, со скобками и пробелами — приведу к одному виду',
                })}
          />
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          busy={busy}
          disabled={!canCall || !parsed.ok}
        >
          Позвонить
        </Button>
      </form>

      {/* Что будет со звонком — сказано прямо: номер сегодняшней базы звонится
          без карточки и метки, любой другой открывается карточкой */}
      <p className="text-xs text-slate-500">
        Номер из сегодняшней базы — без карточки: метка и круг не ставятся. Любой другой номер
        откроется карточкой: метки и заявка — как у выданного. {ownNumbersHint(ownNumbers)}
      </p>

      {error === undefined ? null : (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
    </section>
  );
}
